import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleCheck, Radio, RefreshCw, WifiOff } from 'lucide-react';
import { type DisputeReason } from '@shared/types';
import type { TraderTransaction } from '@/types/api';

import { Panel, PanelHeader, SectionHeading } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { EmptyState, InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { VerificationPrompt } from '@/components/VerificationPrompt';
import { ActivityList } from '@/components/ActivityList';
import { PaymentCard } from '@/components/traffic/PaymentCard';
import { ConfirmDialog } from '@/components/traffic/ConfirmDialog';
import { DisputeDialog } from '@/components/traffic/DisputeDialog';

import { useKyc, useTransactionPage } from '@/lib/queries';
import { useConfirmTransaction, useCreateDispute } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { rub, usdt } from '@/lib/format';
import { NOUN, pluralize } from '@/lib/plural';
import { toPerfTime, type Urgency } from '@/hooks/useCountdown';
import { useNotificationStream, type StreamStatus } from '@/hooks/useNotificationStream';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/cn';

const PENDING_LIMIT = 50;

export default function TrafficPage() {
  const toast = useToast();
  const kyc = useKyc();
  const [pendingOffset,setPendingOffset]=useState(0);
  const [historyOffset,setHistoryOffset]=useState(0);
  const [historyLimit,setHistoryLimit]=useState(100);
  const transactions=useTransactionPage('pending',PENDING_LIMIT,pendingOffset);
  const historyQuery=useTransactionPage('history',historyLimit,historyOffset);
  useEffect(()=>{if(transactions.data && pendingOffset>=transactions.data.total && pendingOffset>0)setPendingOffset(Math.max(0,Math.floor((transactions.data.total-1)/PENDING_LIMIT)*PENDING_LIMIT));},[transactions.data,pendingOffset]);
  useEffect(()=>{if(historyQuery.data && historyOffset>=historyQuery.data.total && historyOffset>0)setHistoryOffset(0);},[historyQuery.data,historyOffset]);
  const confirmMutation = useConfirmTransaction();
  const disputeMutation = useCreateDispute();

  const [confirmTarget, setConfirmTarget] = useState<TraderTransaction | null>(null);
  const [disputeTarget, setDisputeTarget] = useState<TraderTransaction | null>(null);
  const [disputeError, setDisputeError] = useState<string | null>(null);
  // One live region for the whole list: rows report threshold crossings here
  // rather than each owning its own region.
  const [deadlineNotice, setDeadlineNotice] = useState('');

  const announceUrgency = useCallback(
    (transaction: TraderTransaction, urgency: Urgency) => {
      if (urgency === 'calm') return;
      const amount = rub(transaction.amountRub);
      setDeadlineNotice(
        urgency === 'expired'
          ? `Срок по платежу ${amount} от ${transaction.merchantName} истёк.`
          : urgency === 'critical'
            ? `Меньше пяти минут на подтверждение ${amount} от ${transaction.merchantName}.`
            : `Меньше десяти минут на подтверждение ${amount} от ${transaction.merchantName}.`,
      );
    },
    [],
  );

  const onPaymentArrived = useCallback(() => {
    toast.info('Поступил новый платёж', 'Сверьте сумму с банком, прежде чем подтверждать.');
  }, [toast]);

  // Subscribed rather than read imperatively, so a token refresh in another
  // tab rebuilds the stream instead of leaving it authenticated as nobody.
  const accessToken = useAuthStore((state) => state.accessToken);
  const streamStatus = useNotificationStream({
    token: accessToken,
    onPaymentArrived,
  });

  // Convert the query's wall-clock stamp onto the monotonic timeline the
  // countdowns run on, so every card re-syncs together on each poll.
  const syncedAt = useMemo(
    () => toPerfTime(transactions.dataUpdatedAt),
    [transactions.dataUpdatedAt],
  );

  const pending=transactions.data?.items??[];
  const history=historyQuery.data?.items??[];

  const pendingTotalRub = pending.reduce((sum, tx) => sum + tx.amountRub, 0);
  const pendingTotalUsdt = pending.reduce((sum, tx) => sum + tx.amountUsdt, 0);

  const handleDisputeOpen = useCallback((transaction: TraderTransaction) => {
    setDisputeError(null);
    setDisputeTarget(transaction);
  }, []);

  const handleConfirm = async (): Promise<void> => {
    const target = confirmTarget;
    if (!target) return;

    try {
      const result = await confirmMutation.mutateAsync(target);
      setConfirmTarget(null);

      // The released figure is collateral going to the merchant; the commission
      // is the part that becomes the trader's. The old wording («Выпущено N»)
      // named only the first and read like a payout.
      const released = result.releasedUsdt || target.amountUsdt;
      const earned = target.commissionUsdt;
      toast.success(
        'Платёж подтверждён',
        target.traderMarkupRub != null ? 'Из залога выпущено '+usdt(released)+'. Ваша комиссия '+rub(target.traderMarkupRub)+' уже включена в полученный перевод.' : earned === undefined
          ? `${rub(target.amountRub)} от ${target.merchantName}. Из залога выпущено ${usdt(released)}.`
          : `${rub(target.amountRub)} от ${target.merchantName}. Из залога выпущено ${usdt(released)}, ваша комиссия ${usdt(earned)} зачислена в «Доступно к выводу».`,
      );
    } catch (error) {
      setConfirmTarget(null);
      toast.error(
        'Не удалось подтвердить',
        toMessage(error, 'Платёж не подтверждён. Проверьте список и попробуйте снова.'),
      );
    }
  };

  const handleDispute = async (input: {
    reason: DisputeReason;
    statement: string;
    evidence: File[];
  }): Promise<void> => {
    const target = disputeTarget;
    if (!target) return;

    setDisputeError(null);
    try {
      await disputeMutation.mutateAsync({ transactionId: target.id, ...input });
      setDisputeTarget(null);
      toast.success(
        'Заявка отправлена',
        'Её рассмотрит администратор. Платёж заморожен до решения.',
      );
    } catch (error) {
      setDisputeError(toMessage(error, 'Не удалось отправить заявку.'));
    }
  };

  return (
    <div>
      {/* Deadline crossings for the whole list. Polite, and only ever updated on
          a threshold change — never on the per-second tick. */}
      <p role="status" aria-live="polite" className="sr-only">
        {deadlineNotice}
      </p>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-fg">Трафик</h1>
          <p className="mt-1.5 max-w-prose text-sm text-fg-secondary">
            Входящие переводы от мерчантов. Подтвердите каждый в течение тридцати минут после
            поступления или сообщите, что деньги не пришли.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <StreamIndicator status={streamStatus} />
          <Button
            size="sm"
            icon={<RefreshCw size={13} strokeWidth={1.5} aria-hidden="true" />}
            onClick={() => {void transactions.refetch();void historyQuery.refetch();}}
            isLoading={transactions.isFetching && !transactions.isPending}
          >
            Обновить
          </Button>
        </div>
      </header>

      {kyc.data ? <VerificationPrompt kyc={kyc.data} className="mt-6" /> : null}

      {transactions.isError ? (
        <InlineError
          className="mt-6"
          message={toMessage(transactions.error, 'Не удалось загрузить входящие платежи.')}
          onRetry={() => void transactions.refetch()}
        />
      ) : null}

      {/* Awaiting confirmation */}
      <SectionHeading
        actions={
          pending.length > 0 ? (
            <p className="num text-sm text-fg-secondary">
              {pluralize(transactions.data?.total??pending.length, NOUN.payment)} · на странице {rub(pendingTotalRub)} ·{' '}
              {usdt(pendingTotalUsdt)}
            </p>
          ) : undefined
        }
      >
        Ожидают подтверждения
      </SectionHeading>

      <Panel padding="none">
        {transactions.isPending ? (
          <PendingSkeleton />
        ) : pending.length === 0 ? (
          <EmptyState
            icon={CircleCheck}
            title="Ничего не ждёт вашего действия"
            description={
              kyc.data && !kyc.data.canReceiveTraffic
                ? 'Трафик приостановлен, пока верификация не одобрена и ни одна карта не прошла проверку.'
                : 'Со всеми направленными вам платежами уже разобрались. Новые переводы появятся здесь сразу, как только мерчант их отправит — список обновляется сам.'
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {pending.map((transaction) => (
              <PaymentCard
                key={transaction.id}
                transaction={transaction}
                syncedAt={syncedAt}
                // Scoped to the row actually being written, so an in-flight
                // confirmation never blocks action on a second, more urgent
                // payment.
                isBusy={
                  (confirmMutation.isPending &&
                    confirmMutation.variables?.id === transaction.id) ||
                  (disputeMutation.isPending &&
                    disputeMutation.variables?.transactionId === transaction.id)
                }
                onConfirm={setConfirmTarget}
                onDispute={handleDisputeOpen}
                onUrgencyChange={announceUrgency}
              />
            ))}
          </ul>
        )}
      </Panel>

      <PaymentPagination label="Ожидающие платежи" total={transactions.data?.total??0} offset={pendingOffset} limit={PENDING_LIMIT} busy={transactions.isFetching} onChange={setPendingOffset}/>

      {/* Everything else */}
      <SectionHeading actions={<label className="text-sm text-fg-secondary">На странице <select aria-label="Количество операций в истории" className="rounded border border-line bg-surface px-2 py-1" value={historyLimit} onChange={e=>{setHistoryLimit(Number(e.target.value));setHistoryOffset(0);}}>{[50,100,200].map(n=><option key={n} value={n}>{n}</option>)}</select></label>}>История операций</SectionHeading>
      {historyQuery.isError?<InlineError className="mb-4" message={toMessage(historyQuery.error,'Не удалось загрузить историю.')} onRetry={()=>void historyQuery.refetch()}/>:null}

      <Panel padding="none">
        <PanelHeader
          title="История платежей"
          description="Все завершённые и спорные операции. Более ранние доступны на следующих страницах."
        />
        {historyQuery.isPending ? (
          <PendingSkeleton compact />
        ) : history.length === 0 ? (
          <EmptyState
            icon={Radio}
            title="История пока пуста"
            description="Как только вы подтвердите перевод или откроете спор, он появится здесь."
          />
        ) : (
          <ActivityList transactions={history} />
        )}
      </Panel>

      <PaymentPagination label="История платежей" total={historyQuery.data?.total??0} offset={historyOffset} limit={historyLimit} busy={historyQuery.isFetching} onChange={setHistoryOffset}/>

      <ConfirmDialog
        transaction={confirmTarget}
        open={confirmTarget !== null}
        isSubmitting={confirmMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => void handleConfirm()}
      />

      <DisputeDialog
        transaction={disputeTarget}
        open={disputeTarget !== null}
        isSubmitting={disputeMutation.isPending}
        serverError={disputeError}
        onCancel={() => {
          setDisputeTarget(null);
          setDisputeError(null);
        }}
        onSubmit={(input) => void handleDispute(input)}
      />
    </div>
  );
}

const STREAM_COPY: Record<StreamStatus, { label: string; title: string }> = {
  live: { label: 'Онлайн', title: 'Соединение установлено — новые платежи появляются сразу.' },
  connecting: { label: 'Подключение', title: 'Устанавливаем живое соединение.' },
  offline: {
    label: 'Опрос',
    title: 'Живое соединение недоступно. Список всё равно обновляется каждые 5 секунд.',
  },
};

function StreamIndicator({ status }: { status: StreamStatus }) {
  const { label, title } = STREAM_COPY[status];

  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5',
        'whitespace-nowrap text-2xs font-semibold uppercase tracking-wide',
        status === 'live'
          ? 'border-ok-500 bg-ok-50 text-ok-700'
          : 'border-line bg-sunken text-fg-muted',
      )}
    >
      {status === 'offline' ? (
        <WifiOff size={11} strokeWidth={1.5} aria-hidden="true" />
      ) : (
        <Radio size={11} strokeWidth={1.5} aria-hidden="true" />
      )}
      {label}
      <span className="sr-only">. {title}</span>
    </span>
  );
}

function PendingSkeleton({ compact = false }: { compact?: boolean }) {
  return (
    <div className="divide-y divide-line" role="status" aria-busy="true">
      <span className="sr-only">Загружаю платежи</span>
      {Array.from({ length: compact ? 4 : 2 }, (_, index) => (
        <div key={index} className="flex flex-wrap gap-6 px-5 py-5">
          <div className="min-w-[10rem] flex-1">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className={cn('mt-2', compact ? 'h-4 w-32' : 'h-8 w-40')} />
            <Skeleton className="mt-2 h-3 w-28" />
          </div>
          {!compact ? (
            <div className="min-w-[12rem] flex-1 space-y-2">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-4/5" />
              <Skeleton className="h-3.5 w-3/5" />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}


function PaymentPagination({label,total,offset,limit,busy,onChange}:{label:string;total:number;offset:number;limit:number;busy:boolean;onChange:(offset:number)=>void}) {
 if(total===0)return null;
 return <nav aria-label={label} className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-fg-secondary">
  <span>{offset+1}–{Math.min(offset+limit,total)} из {total}</span>
  <div className="flex gap-2"><Button size="sm" disabled={busy||offset===0} onClick={()=>onChange(Math.max(0,offset-limit))}>Назад</Button><Button size="sm" disabled={busy||offset+limit>=total} onClick={()=>onChange(offset+limit)}>Далее</Button></div>
 </nav>;
}
